import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Key,
  Check,
  X,
  Crown,
  Calendar,
  Clock,
  Gift,
  History,
  ExternalLink,
  Loader2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

const PROBODY_PAYMENT_URL = "https://probodysystems.com/fiyatlandirma";

export default function ActivationCodeModule({ businessId, onSubscriptionUpdate }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [activationCode, setActivationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [codeHistory, setCodeHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlans();
    if (businessId) {
      fetchSubscription();
      fetchCodeHistory();
    }
  }, [businessId]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/public/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(Array.isArray(data) ? data : (data.plans || []));
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const getPlanName = (planId) => {
    if (planId === "free") return t('dashboard:subscription.free');
    const plan = plans.find(p => p.id === planId);
    return plan?.name || planId;
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/subscription`, {
        credentials: "include",
      });
      if (response.ok) {
        setSubscription(await response.json());
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const fetchCodeHistory = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/activation-history`, {
        credentials: "include",
      });
      if (response.ok) {
        setCodeHistory(await response.json());
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const formatCode = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    // Format as XXXX-XXXX-XXXX-XXXX
    const parts = [];
    for (let i = 0; i < cleaned.length && i < 16; i += 4) {
      parts.push(cleaned.slice(i, i + 4));
    }
    return parts.join("-");
  };

  const handleCodeChange = (e) => {
    setActivationCode(formatCode(e.target.value));
  };

  const handleActivateCode = async () => {
    if (!activationCode || activationCode.replace(/-/g, "").length < 12) {
      toast.error(t("admin:toast_lutfenGecerliBirAktivasyonKodu"));
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${API}/activation-codes/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          code: activationCode.replace(/-/g, ""),
          business_id: businessId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`🎉 ${data.plan_name} planı aktifleştirildi!`);
        setActivationCode("");
        fetchSubscription();
        fetchCodeHistory();
        if (onSubscriptionUpdate) onSubscriptionUpdate();
      } else {
        toast.error(data.detail || t("admin:activationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setVerifying(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case "pro": return "bg-blue-100 text-blue-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      {/* Current Subscription Status */}
      {subscription && (
        <Card className={`border-2 ${
          subscription.plan !== "free" ? "border-green-200 bg-green-50" : "border-gray-200"
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  subscription.plan !== "free" ? "bg-green-100" : "bg-gray-100"
                }`}>
                  <Crown className={`w-5 h-5 ${
                    subscription.plan !== "free" ? "text-green-600" : "text-gray-500"
                  }`} />
                </div>
                <div>
                  <p className="font-semibold text-navy">
                    {t('admin:currentPlanLabel')}: {getPlanName(subscription.plan)}
                  </p>
                  {subscription.expires_at && (
                    <p className="text-sm text-grey flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {t('dashboard:subscription.expiry')}: {format(new Date(subscription.expires_at), "dd/MM/yyyy", { locale: getDateLocale() })}
                    </p>
                  )}
                </div>
              </div>
              <Badge className={subscription.is_active && subscription.plan !== "free" 
                ? "bg-green-100 text-green-700" 
                : "bg-gray-100 text-gray-600"
              }>
                {subscription.is_active ? (subscription.plan !== "free" ? t('admin:active') : t('dashboard:subscription.free')) : t('common:passive')}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activation Code Entry */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5 text-navy" />
            {t('dashboard:activation.enterActivationCode')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="activation-code">{t('dashboard:activation.activationCode')}</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="activation-code"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={activationCode}
                onChange={handleCodeChange}
                className="font-mono text-lg tracking-wider rounded-xl"
                maxLength={19}
              />
              <Button
                onClick={handleActivateCode}
                disabled={verifying || !activationCode}
                className="bg-navy hover:bg-navy/90 rounded-xl px-6"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {t('dashboard:activation.activate')}
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-grey mt-2">
              {t('dashboard:activation.enterCodeHint')}
            </p>
          </div>

          {/* Get Code Button */}
          <div className="flex items-center gap-4 pt-4 border-t">
            <Button
              variant="outline"
              className="flex-1 rounded-xl"
              onClick={() => window.open(PROBODY_PAYMENT_URL, "_blank")}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('dashboard:activation.buyPackage')}
            </Button>
            <Button
              variant="ghost"
              className="rounded-xl"
              onClick={() => setShowHistoryModal(true)}
            >
              <History className="w-4 h-4 mr-2" />
              {t('dashboard:activation.history')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">{t('dashboard:activation.howToGetCode')}</p>
              <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                <li>{t('dashboard:activation.step1')}</li>
                <li>{t('dashboard:activation.step2')}</li>
                <li>{t('dashboard:activation.step3')}</li>
                <li>{t('dashboard:activation.step4')}</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Code History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              {t('dashboard:activation.activationHistory')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-80 overflow-y-auto">
            {codeHistory.length > 0 ? (
              codeHistory.map((item) => (
                <div
                  key={item.code_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono bg-white px-2 py-0.5 rounded">
                        {item.code_display}
                      </code>
                      <Badge className={getPlanBadgeColor(item.plan)}>
                        {getPlanName(item.plan)}
                      </Badge>
                    </div>
                    <p className="text-xs text-grey mt-1">
                      {item.used_at && format(new Date(item.used_at), "dd/MM/yyyy, HH:mm", { locale: getDateLocale() })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{item.duration_days} gün</p>
                    {item.is_gift && (
                      <Badge className="bg-pink-100 text-pink-600 text-xs">
                        <Gift className="w-3 h-3 mr-1" />
                        Hediye
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-grey">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t('dashboard:activation.noHistory')}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
